import React, { useEffect, useState, MouseEvent } from "react";
import { createPortal } from "react-dom";
import { X, Linkedin as LinkedinIcon, Twitter as TwitterIcon, Github as GithubIcon, Mail } from "lucide-react";

interface StaffMember {
  name: string;
  role: string;
  image: string;
  alt: string;
  bio?: string;
  skills?: string[];
  social?: {
    linkedin?: string;
    twitter?: string;
    github?: string;
    email?: string;
  };
}

interface Props {
  member: StaffMember;
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
}

const StaffBio: React.FC<Props> = ({ member, isOpen, setIsOpen }) => {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isOpen && member) {
      setMounted(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setVisible(true);
        });
      });
    } else {
      setVisible(false);
      const timer = setTimeout(() => setMounted(false), 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen, member]);

  if (!mounted) return null;

  const handleBackdropClick = () => {
    setIsOpen(false);
  };

  const stopPropagation = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  const modalContent = (
    <div
      className={`fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 transition-opacity duration-200 ${
        visible ? "bg-black/60 backdrop-blur-sm opacity-100" : "opacity-0 pointer-events-none"
      }`}
      onClick={handleBackdropClick}
    >
      <div
        className={`relative transform overflow-hidden rounded-2xl bg-dark-bg border border-white/10 text-left shadow-2xl transition-all duration-200 mt-20 w-full sm:max-w-lg ${
          visible ? "opacity-100 scale-100" : "opacity-0 scale-95"
        }`}
        onClick={stopPropagation}
      >
        <button
          onClick={() => setIsOpen(false)}
          className="absolute top-4 right-4 text-gray-400 hover:text-white bg-white/5 hover:bg-white/10 rounded-lg p-2 transition-all duration-200 z-10"
          aria-label="Close modal"
        >
          <X size={20} />
        </button>

        {/* modal content */}
        <div className="bg-gradient-to-b from-white/5 to-transparent px-6 pt-8 pb-6 sm:p-8">
          <div className="flex flex-col items-center text-center">
            <div className="relative mb-6">
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-[#00A3FF] to-[#0077CC] opacity-20 blur-2xl"></div>
              <img
                src={member.image}
                alt={member.alt}
                className="relative w-32 h-32 sm:w-40 sm:h-40 rounded-full object-cover shadow-xl border-4 border-white/20"
              />
            </div>

            {/* name */}
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
              {member.name}
            </h2>

            {/* role */}
            <p className="text-base sm:text-lg text-[#00A3FF] font-medium mb-6">
              {member.role}
            </p>

            {/* skills */}
            {Array.isArray(member.skills) && member.skills.length > 0 && (
              <div className="w-full mb-6">
                <div className="flex flex-wrap gap-2 justify-center">
                  {member.skills.map((skill) => (
                    <span
                      key={skill}
                      className="bg-[#00A3FF]/10 border border-[#00A3FF]/30 text-[#00A3FF] text-xs sm:text-sm px-3 py-1.5 rounded-full"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* bio */}
            <div className="mb-6 w-full">
              <p className="text-sm sm:text-base text-gray-300 leading-relaxed">
                {member.bio || "More details coming soon."}
              </p>
            </div>

            {/* socials */}
            {member.social && Object.keys(member.social).length > 0 && (
              <div className="flex gap-3">
                {member.social.linkedin && (
                  <a
                    href={member.social.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-[#00A3FF] bg-white/5 hover:bg-white/10 rounded-lg p-2.5 transition-all duration-200"
                    aria-label="LinkedIn"
                  >
                    <LinkedinIcon size={20} />
                  </a>
                )}
                {member.social.twitter && (
                  <a
                    href={member.social.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-[#00A3FF] bg-white/5 hover:bg-white/10 rounded-lg p-2.5 transition-all duration-200"
                    aria-label="Twitter"
                  >
                    <TwitterIcon size={20} />
                  </a>
                )}
                {member.social.github && (
                  <a
                    href={member.social.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-[#00A3FF] bg-white/5 hover:bg-white/10 rounded-lg p-2.5 transition-all duration-200"
                    aria-label="GitHub"
                  >
                    <GithubIcon size={20} />
                  </a>
                )}
                {member.social.email && (
                  <a
                    href={`mailto:${member.social.email}`}
                    className="text-gray-400 hover:text-[#00A3FF] bg-white/5 hover:bg-white/10 rounded-lg p-2.5 transition-all duration-200"
                    aria-label="Email"
                  >
                    <Mail size={20} />
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.querySelector("#portal")!);
};

export default StaffBio;
